import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";

import {
  getProductOptionTypesData,
  getProductVariantsData,
  orderbyOptionTypes,
  getVariantsExcludingMaster,
  getDefaultMultipleVariantIndex,
} from "@helpers/product-details";

import { useProductDetailContext } from "@contexts/product-detail-context";
import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";

import useGetCartAddedVariant from "@hooks/product-detail/use-get-cart-added-variant";

import { isGiftCardProduct } from "@helpers/products/types";

import { getIsPricelessShop } from "@selectors/store";

import { DEFAULT_QUANTITY } from "@constants/products/detail";

const useProductDetailMultipleVariantsState = ({ product, masterVariant }) => {
  const { formatPriceInDisplayStoreCurrency } = useStoreCurrencyConverter();
  const [optionTypeVariantMapping, setOptionTypeVariantMapping] = useState({});
  const [productQuantity, setProductQuantity] = useState(DEFAULT_QUANTITY);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const {
    editProductIds: [editProductId],
  } = useProductDetailContext();

  const optionTypesData = useMemo(
    () => orderbyOptionTypes(getProductOptionTypesData(product), "priceIn"),
    [product],
  );

  const variantsData = useMemo(() => getProductVariantsData(optionTypesData), [optionTypesData]);

  const { intent, variantsIncludingMaster } = product;
  const cartAddedVariant = useGetCartAddedVariant({ intent });
  const isPricelessShop = useSelector(getIsPricelessShop);

  const variantValues = useMemo(() => {
    const values = isGiftCardProduct(intent)
      ? getVariantsExcludingMaster(variantsIncludingMaster)
          .map((item) => variantsData[item.optionValues[0].id])
          .filter(Boolean)
      : Object.values(variantsData);

    return orderBy(values, "priceIn", "asc").map((v, i) => ({ ...v, index: i }));
  }, [variantsData, variantsIncludingMaster, intent]);

  useEffect(() => {
    if (!isEmpty(variantValues)) {
      const defaultIndex = getDefaultMultipleVariantIndex({
        variantValues,
        cartAddedVariant,
        isPricelessShop,
        intent,
      });

      if (defaultIndex < 0) {
        return;
      }

      if (cartAddedVariant?.productId != null) {
        const cartVariantIndex = variantValues.findIndex(
          (variant) => variant.externalId.toString() === cartAddedVariant.productId?.toString(),
        );
        if (cartVariantIndex === defaultIndex) {
          setProductQuantity(cartAddedVariant.quantity);
        }
      }

      const defaultVariant = variantValues[defaultIndex];
      if (defaultVariant) {
        setOptionTypeVariantMapping(defaultVariant);
      }
    } else if (masterVariant) setOptionTypeVariantMapping(masterVariant);
  }, [variantValues, masterVariant, cartAddedVariant, isPricelessShop, intent]);

  //to auto select slider image and reset quantity whenever variant is selected.
  useEffect(() => {
    if (optionTypeVariantMapping?.index != null) {
      setSelectedImageIndex(optionTypeVariantMapping.index);
    }
    !editProductId && setProductQuantity(DEFAULT_QUANTITY);
  }, [optionTypeVariantMapping, cartAddedVariant, editProductId]);

  //to select variant corresponding to the selected slider image.
  const handleImageChange = (index) => {
    if (variantValues[index]) setOptionTypeVariantMapping(variantValues[index]);
    setSelectedImageIndex(index);
  };

  const getDisplayPrice = () => {
    const variantValuesLength = variantValues.length;
    if (variantValuesLength > 1) {
      const minPrice = formatPriceInDisplayStoreCurrency(variantValues[0].priceIn);
      const maxPrice = formatPriceInDisplayStoreCurrency(
        variantValues[variantValuesLength - 1].priceIn,
      );
      return `${minPrice} - ${maxPrice}`;
    }
    return formatPriceInDisplayStoreCurrency(optionTypeVariantMapping.priceIn || product.priceIn);
  };

  return {
    optionTypeVariantMapping,
    setOptionTypeVariantMapping,
    productQuantity,
    setProductQuantity,
    selectedImageIndex,
    setSelectedImageIndex: handleImageChange,
    optionTypesData,
    variantsData,
    variantValues,
    getDisplayPrice,
  };
};

export default useProductDetailMultipleVariantsState;
